import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  untracked,
  viewChild,
} from '@angular/core';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HdGateActivationChipComponent } from '../../bodygraph/gate-activation-chip/hd-gate-activation-chip.component';
import { HdPropertyTileComponent } from '../../bodygraph/basic-properties/hd-property-tile.component';
import {
  HD_CHART_MATCH_LIST_IDLE,
  HdChartMatchListComponent,
  HdChartMatchListState,
} from '../../library/hd-chart-match-list.component';
import { HdChartMatchesService } from '../../library/hd-chart-matches.service';
import { HdChartMatchToggleComponent } from '../../library/hd-chart-match-toggle.component';
import { HdChartSearchContextService } from '../../library/hd-chart-search-context.service';
import { hdRevealWhileGrowing } from '../../library/hd-reveal-while-growing';
import {
  HD_LOCKS_KEYS_SEARCH_KINDS,
  HdLocksKeysSearchKind,
  HdLocksKeysSearchMatch,
  HdLocksKeysSearchRelation,
  arrangeHdLocksKeysSearchMatches,
  buildHdLocksKeysSearchCandidates,
  collectHdLocksKeysSearchMatches,
  hdLocksKeysSearchGates,
} from './hd-locks-keys-chart-search';
import { HdLocksKeysChartSearchStore } from './hd-locks-keys-chart-search.store';
import {
  HD_LOCKS_KEYS_LOCKS,
  HdLocksKeysSearchFilter,
  HdLocksKeysSearchSort,
  hdLocksKeysKeyGate,
} from './hd-locks-keys-metadata';
import { hdLocksKeysPeriodText } from './hd-locks-keys-period-text';
import { HdLocksKeysStateService } from './hd-locks-keys-state.service';

/**
 * Zoektocht vanuit Locks & Keys (spec 2026-10-21-poortactivatie-zoektochten
 * §4.6): bibliotheek-charts met de key voor de locks van de actieve chart, met
 * het slot voor zijn keys, of beide, in de gekozen periode van de tweeluik-
 * toggle. Per treffer de relaties: de poort van de actieve chart als nummer,
 * die van de treffer als planeetchips in de zijdekleur. Gebruikt in het
 * L&K-paneel en in de L&K-sectie van Geavanceerd.
 *
 * Staat per chart ({@link HdLocksKeysChartSearchStore}): een chartwissel start
 * nooit vanzelf een zoektocht. Een chart waar nog niet gezocht werd, toont de
 * sectie dicht; een chart waar ze open bleef, toont de bewaarde treffers zonder
 * nieuwe zoektocht (zolang periode, zoektocht en scope niet wijzigden).
 * Sortering en kruisfilter zijn bewaarde weergavekeuzes en werken client-side.
 *
 * Enkel op de chart-pagina (die opent de treffers) en niet op een composite-
 * tab ("mijn chart" is daar dubbelzinnig); op een cycle-tab telt de natal kant
 * (`chartActivations` van de state).
 */
@Component({
  selector: 'hd-locks-keys-chart-search',
  standalone: true,
  imports: [
    ESegmentedToggleComponent,
    HdChartMatchListComponent,
    HdChartMatchToggleComponent,
    HdGateActivationChipComponent,
    HdPropertyTileComponent,
  ],
  templateUrl: './hd-locks-keys-chart-search.component.html',
  styleUrl: './hd-locks-keys-chart-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysChartSearchComponent {
  protected readonly state = inject(HdLocksKeysStateService);
  private readonly context = inject(HdChartSearchContextService);
  private readonly store = inject(HdLocksKeysChartSearchStore);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  /**
   * Voor de matches-service (en via de bibliotheek de HTTP-laag) pas bij de eerste
   * zoekopdracht, en voor `afterNextRender` buiten de constructor.
   */
  private readonly injector = inject(Injector);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.gateInfo.locksKeysTextIn(key, this.lang(), params);
  }

  private readonly myGates = computed(() => [...new Set(this.state.chartActivations().map(activation => activation.gate))]);

  protected readonly available = computed(() =>
    this.context.chartOpener() != null && !this.context.compositeActive() && this.myGates().length > 0);

  protected readonly chartKey = computed(() => this.context.activeChartKey());
  private readonly entry = computed(() => this.store.entry(this.chartKey()));
  protected readonly open = computed(() => this.entry().open);
  protected readonly kind = computed(() => this.entry().kind);

  /** De gezochte relaties: leeg = de actieve chart heeft niets om mee te zoeken. */
  private readonly candidates = computed(() =>
    buildHdLocksKeysSearchCandidates(this.kind(), this.state.periodId(), this.myGates()));
  protected readonly hasCandidates = computed(() => this.candidates().length > 0);

  /** Alles waarvan de treffers afhangen; een bewaard resultaat geldt enkel bij dezelfde signatuur. */
  private readonly signature = computed(() => JSON.stringify({
    candidates: this.candidates(),
    scope: this.context.matchScope(),
    exclude: this.context.excludeChartIds(),
    source: this.context.relationSourceChartId(),
  }));

  /** Ruwe treffers van de huidige zoektocht (verouderde resultaten tellen niet). */
  private readonly rawState = computed<HdChartMatchListState<HdLocksKeysSearchMatch>>(() => {
    const result = this.entry().result;
    return result != null && result.signature === this.signature() ? result.state : HD_CHART_MATCH_LIST_IDLE;
  });

  /** Weergave: kruisfilter en sortering, zonder nieuwe zoektocht. */
  protected readonly listState = computed<HdChartMatchListState<HdLocksKeysSearchMatch>>(() => {
    const raw = this.rawState();
    if (raw.status !== 'loaded') return raw;
    return { status: 'loaded', items: arrangeHdLocksKeysSearchMatches(raw.items, this.state.searchFilter(), this.state.searchSort()) };
  });

  protected readonly kindOptions = computed<readonly ESegmentedToggleOption<HdLocksKeysSearchKind>[]>(() =>
    HD_LOCKS_KEYS_SEARCH_KINDS.map(kind => ({
      value: kind,
      label: this.t(`search.kinds.${kind}`),
      title: this.t(`search.kindTooltips.${kind}`),
    })));

  protected readonly sortOptions = computed<readonly ESegmentedToggleOption<HdLocksKeysSearchSort>[]>(() => [
    { value: 'relations', label: this.t('search.sortRelations'), title: this.t('search.sortRelationsTooltip') },
    { value: 'name', label: this.t('search.sortName'), title: this.t('search.sortNameTooltip') },
  ]);

  protected readonly filterOptions = computed<readonly ESegmentedToggleOption<HdLocksKeysSearchFilter>[]>(() => [
    { value: 'all', label: this.t('search.filterAll'), title: this.t('search.filterAllTooltip') },
    { value: 'cross', label: this.t('search.filterCross'), title: this.t('search.filterCrossTooltip') },
  ]);

  /**
   * Tegelwaarde (zoals de waarde van de profiel-tegel): hoeveel locks en keys de
   * actieve chart in de gekozen periode zelf draagt, en welke periode.
   */
  protected readonly summary = computed(() => {
    const mine = new Set(this.myGates());
    const periodId = this.state.periodId();
    const locks = HD_LOCKS_KEYS_LOCKS.filter(lock => mine.has(lock.gate)).length;
    const keys = HD_LOCKS_KEYS_LOCKS.filter(lock => mine.has(hdLocksKeysKeyGate(lock.gate, periodId))).length;
    const period = hdLocksKeysPeriodText(this.gateInfo, this.lang(), periodId).label;
    return this.t('search.summary', { locks, keys, period });
  });

  protected readonly hint = computed(() => {
    const period = hdLocksKeysPeriodText(this.gateInfo, this.lang(), this.state.periodId()).label;
    return this.t(this.context.matchScope() === 'related' ? 'search.hintRelated' : 'search.hint', { period });
  });

  protected readonly labels = computed(() => ({
    loading: this.t('search.loading'),
    empty: this.t(this.state.searchFilter() === 'cross' ? 'search.emptyCross' : 'search.empty'),
    error: this.t('search.error'),
  }));

  protected readonly isMutual = (match: HdLocksKeysSearchMatch): boolean => match.mutual;

  private searchTimer: number | undefined;

  /**
   * Zoeken wanneer de sectie van deze chart open staat maar geen geldig resultaat
   * heeft: bij openen, of bij een andere zoektocht/periode/scope terwijl ze open
   * staat. Uitgesteld tot na de lopende wijzigingsronde: bij een chartwissel komen
   * de chart-sleutel en de activaties in aparte effecten binnen, en een tussenstand
   * (nieuwe chart, oude poorten) mag geen zoektocht starten of een bewaard
   * resultaat overschrijven.
   */
  private readonly scheduleSearch = effect(() => {
    this.chartKey();
    this.entry();
    this.signature();
    this.available();
    untracked(() => {
      clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.searchIfNeeded());
    });
  });

  private readonly tile = viewChild('tile', { read: ElementRef });
  private stopReveal: (() => void) | null = null;

  private readonly cleanup = inject(DestroyRef).onDestroy(() => {
    clearTimeout(this.searchTimer);
    this.stopReveal?.();
  });

  protected toggle(): void {
    const open = !this.open();
    this.store.update(this.chartKey(), { open });
    if (open) {
      // De zoekinhoud verschijnt onder de tegel, vaak onder de rand van het paneel:
      // meescrollen terwijl ze groeit (laden → treffers), anders lijkt er niets te gebeuren.
      afterNextRender(() => {
        const element = this.tile()?.nativeElement as HTMLElement | undefined;
        if (!element) return;
        this.stopReveal?.();
        this.stopReveal = hdRevealWhileGrowing(element);
      }, { injector: this.injector });
    } else {
      this.stopReveal?.();
    }
  }

  protected close(): void {
    this.stopReveal?.();
    this.store.update(this.chartKey(), { open: false });
  }

  protected setKind(kind: HdLocksKeysSearchKind): void {
    this.store.update(this.chartKey(), { kind });
  }

  protected setSort(sort: HdLocksKeysSearchSort): void {
    this.state.searchSort.set(sort);
  }

  protected setFilter(filter: HdLocksKeysSearchFilter): void {
    this.state.searchFilter.set(filter);
  }

  protected openMatch(item: PlatformChartListItemDto): void {
    if (item.id) this.context.chartOpener()?.(item.id);
  }

  protected relationTooltip(match: HdLocksKeysSearchMatch, relation: HdLocksKeysSearchRelation): string {
    const params = {
      name: match.item.name ?? '',
      key: relation.keyGate,
      lock: relation.lockGate,
      role: this.t(`locks.${relation.lockGate}.role`),
    };
    return this.t(relation.direction === 'theirKey' ? 'search.theirKeyTooltip' : 'search.theirLockTooltip', params);
  }

  protected relationTrack(relation: HdLocksKeysSearchRelation): string {
    return `${relation.direction}-${relation.lockGate}`;
  }

  private searchIfNeeded(): void {
    const chartKey = this.chartKey();
    const entry = this.store.entry(chartKey);
    const signature = this.signature();
    const candidates = this.candidates();
    if (!this.available() || !entry.open || candidates.length === 0) return;
    // Bewaard of al onderweg (ook vanuit de andere plek waar deze sectie staat).
    if (entry.result?.signature === signature) return;
    void this.search(chartKey, signature, candidates);
  }

  private async search(
    chartKey: string | null,
    signature: string,
    candidates: ReturnType<typeof buildHdLocksKeysSearchCandidates>,
  ): Promise<void> {
    this.store.update(chartKey, { result: { signature, state: { status: 'loading', items: [] } } });
    let state: HdChartMatchListState<HdLocksKeysSearchMatch>;
    try {
      const matches = await this.injector.get(HdChartMatchesService).findGateMatches({
        gates: hdLocksKeysSearchGates(candidates),
        match: 'any',
        scope: this.context.matchScope(),
        sourceChartId: this.context.relationSourceChartId(),
        excludeChartIds: this.context.excludeChartIds(),
      });
      state = { status: 'loaded', items: collectHdLocksKeysSearchMatches(matches.map(match => match.item), candidates) };
    } catch {
      state = { status: 'error', items: [] };
    }
    // Bij de chart van herkomst bewaren, tenzij daar intussen een andere zoektocht liep.
    if (this.store.entry(chartKey).result?.signature === signature) {
      this.store.update(chartKey, { result: { signature, state } });
    }
  }
}
